/**
 * Project schemas — the top-level entity. A Project carries the SAP/Salesforce
 * linkage, the requesting PM, the site, the budget, the status, and child Tasks.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, MoneyCentsSchema, PageEnvelope, PageQuerySchema, PhoneSchema, UuidSchema, UlidSchema, } from './common.js';
import { AddressSchema } from './locations.js';
import { IntakeProposedTaskSchema } from './tasks.js';
import { CrewRoleLinesSchema } from './labor-rates.js';
import { MileageItinerarySchema } from './mileage.js';
import { PrioritySchema, ProjectKindSchema, ProjectSourceSchema, ProjectStatusSchema, } from './enums.js';
// ----------------------------------------------------------------------------
// Per-day working schedule (multi-day projects)
// ----------------------------------------------------------------------------
/**
 * One day's working window. A project's `requested_schedule` (PM) and
 * `approved_schedule` (supervisor) are arrays of these — the crew's hours per
 * day, which may be the same across the project or differ day-to-day.
 */
export const ScheduleDaySchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
    start_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM'),
    end_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM'),
});
/**
 * Input-side validation for a per-day schedule: each day's window must end
 * after it starts, and no date may repeat. Only the *write* payloads use this
 * — response schemas stay on the permissive ScheduleDaySchema so a legacy DB
 * row can never 500 a read. Routes sort by date before deriving bounds.
 */
export const ScheduleDaysInputSchema = z
    .array(ScheduleDaySchema)
    .superRefine((days, ctx) => {
    const seen = new Set();
    days.forEach((d, i) => {
        if (d.end_time <= d.start_time) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [i, 'end_time'],
                message: 'end_time must be after start_time',
            });
        }
        if (seen.has(d.date)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [i, 'date'],
                message: `duplicate date ${d.date}`,
            });
        }
        seen.add(d.date);
    });
});
// ----------------------------------------------------------------------------
// Project
// ----------------------------------------------------------------------------
/** How a project's bid is used (demo #5 follow-up): not used at all, captured for
 *  internal budget/analytics only, or sent to the requestor for approval. */
export const BidModeSchema = z.enum(['none', 'internal', 'approval']);
/** A person who receives project + task status-update emails (directory-picked
 *  or a custom contact). */
export const StatusUpdateRecipientSchema = z.object({
    name: z.string().min(1).max(200),
    email: z.string().email().max(200),
});
export const ProjectSchema = z.object({
    id: UuidSchema,
    // Identity / linkage to SAP & Salesforce
    sap_order_number: z.string().nullable(),
    sap_project_id: z.string().nullable(),
    customer_number: z.string().nullable(),
    design_job_number: z.string().nullable(),
    design_pd_number: z.string().nullable(),
    /** Parent project this one follows (e.g. a punch visit after its install). */
    parent_project_id: UuidSchema.nullable(),
    // Headline / display
    title: z.string().min(1).max(200),
    client_name: z.string().min(1).max(200),
    source: ProjectSourceSchema,
    /** Category of field work — a behavioral tag, not a structural fork. */
    kind: ProjectKindSchema.default('install'),
    priority: PrioritySchema.default('normal'),
    status: ProjectStatusSchema.default('scheduled'),
    pre_blocked_status: ProjectStatusSchema.nullable().describe('when status=blocked, the previous status so we can resume'),
    // Site
    site_location_id: UuidSchema.nullable(),
    site_address: AddressSchema.nullable(),
    site_contact_name: z.string().nullable(),
    site_contact_phone: PhoneSchema.nullable(),
    /** Site access / location notes — gate codes, "between two doctors' offices",
     *  parking, dock access. Distinct from the project-level intake_notes. */
    site_notes: z.string().nullable(),
    /**
     * Pickup origin — a canonical Vari warehouse name (Northlake Warehouse,
     * VariSpace Southlake/Coppell, Dark Warehouse, …) or a PM-entered "Other".
     * Resolved from the packing slip at intake, confirmed in the review. A list:
     * a project can pull inventory from more than one warehouse (VCS-203).
     */
    origin_warehouse: z.array(z.string().max(200)).nullable(),
    // Internal team attribution
    pm_user_id: UuidSchema.nullable(),
    /** Resolved PM display name. Populated by the list endpoint; omitted elsewhere. */
    pm_display_name: z.string().nullable().optional(),
    /** Resolved PM email — populated by GET /projects/:id so the bid recipient can
     *  default to the requesting PM (§A6). Omitted by endpoints that don't join. */
    pm_email: z.string().nullable().optional(),
    interior_designer: z.string().nullable(),
    interior_designer_email: z.string().nullable(),
    csm: z.string().nullable(),
    csm_email: z.string().nullable(),
    sales_executive: z.string().nullable(),
    sales_executive_email: z.string().nullable(),
    /** Ad-hoc people who receive project + task status-update emails (in addition
     *  to the role contacts above) — directory-picked or custom name + email. */
    status_update_recipients: z.array(StatusUpdateRecipientSchema).nullable(),
    // Money
    delivery_install_budget_cents: MoneyCentsSchema.nullable(),
    actual_cost_cents: MoneyCentsSchema.nullable(),
    // Bid (§A) — supervisor's quoted cost (labor + mileage + debris)
    bid_labor_cents: MoneyCentsSchema.nullable(),
    // Labor breakdown (§A6f) — when built from a crew: per-role lines (count +
    // hours), plus the derived crew total, total man-hours (bid_labor_hours), and
    // blended rate. All null for a flat-amount labor bid.
    bid_labor_roles: CrewRoleLinesSchema.nullable(),
    bid_labor_hours: z.number().nonnegative().nullable(),
    bid_labor_crew: z.number().int().min(0).nullable(),
    bid_labor_rate_cents: MoneyCentsSchema.nullable(),
    bid_mileage_miles: z.number().nonnegative().nullable(),
    bid_mileage_cents: MoneyCentsSchema.nullable(),
    /** Saved multi-leg journey behind the round-trip miles (§A6g) — null when the
     *  miles were entered manually. The miles field is derived from this when set. */
    bid_mileage_legs: MileageItinerarySchema.nullable(),
    bid_debris_quarters: z.number().int().min(0).max(4).nullable(),
    bid_debris_cents: MoneyCentsSchema.nullable(),
    bid_total_cents: MoneyCentsSchema.nullable(),
    bid_show_cost: z.boolean(),
    bid_recipient_name: z.string().nullable(),
    bid_recipient_email: z.string().nullable(),
    bid_approved_at: IsoDateTimeSchema.nullable(),
    bid_sent_at: IsoDateTimeSchema.nullable(),
    /** Whether the bid requires the requestor's approval, is internal-only, or is
     *  unused (demo #5 follow-up). Drives the approve-&-schedule flow. */
    bid_mode: BidModeSchema,
    // Notes
    intake_notes: z.string().nullable(),
    internal_notes: z.string().nullable(),
    /** Closeout narrative shown in the recap PDF + "recap available" message (F.4). */
    recap_narrative: z.string().nullable(),
    // Status page slug (opaque, shareable, no-login)
    status_page_slug: UlidSchema,
    // Scheduling window. requested_start_date/end_date are the bounds (kept in
    // sync with the schedules' first/last day); the *_schedule arrays carry the
    // per-day working hours for multi-day projects.
    requested_start_date: IsoDateTimeSchema.nullable(),
    requested_end_date: IsoDateTimeSchema.nullable(),
    deadline_date: IsoDateTimeSchema.nullable(),
    /** PM's requested per-day hours; null when not specified (legacy / single time). */
    requested_schedule: z.array(ScheduleDaySchema).nullable(),
    /** When true the PM left working hours flexible ("come whenever you can") —
     *  the schedule day windows are advisory placeholders. */
    requested_schedule_flexible: z.boolean().default(false),
    /** Supervisor's committed per-day hours, set on approval. */
    approved_schedule: z.array(ScheduleDaySchema).nullable(),
    /** Supervisor's PROPOSED per-day hours awaiting the PM's confirmation — a
     *  sub-state of 'requested'. Set by `propose`, cleared when the PM confirms
     *  (→ approved_schedule, status 'scheduled') or the request is sent back. */
    proposed_schedule: z.array(ScheduleDaySchema).nullable(),
    proposed_at: IsoDateTimeSchema.nullable(),
    // Approval (PM request → supervisor approve)
    approver_user_id: UuidSchema.nullable(),
    approved_at: IsoDateTimeSchema.nullable(),
    // Send-back-for-revision (SCH.5): the supervisor's note + optional proposed
    // start, and when they bounced it. revision_requested_at set = awaiting the
    // PM's revision (still status 'requested'); cleared on resubmit.
    revision_note: z.string().nullable(),
    revision_proposed_start: IsoDateTimeSchema.nullable(),
    revision_requested_at: IsoDateTimeSchema.nullable(),
    /** The bounce includes the bid total for the requestor to see (§A6c). */
    revision_show_budget: z.boolean(),
    // Lifecycle timestamps
    created_at: IsoDateTimeSchema,
    updated_at: IsoDateTimeSchema,
    completed_at: IsoDateTimeSchema.nullable(),
    /** Direct project work (D-3/A3): first crew Start → on_site. */
    started_at: IsoDateTimeSchema.nullable().optional(),
    /** Crew Finish → pending_closeout; the undo grace window keys off this. */
    finished_at: IsoDateTimeSchema.nullable().optional(),
    /** Soft-delete timestamp; non-null = deleted (restorable). */
    deleted_at: IsoDateTimeSchema.nullable(),
});
/**
 * Crew Start / Finish on a PROJECT (D-3/A3): `on_site` = started ("In
 * progress"), `pending_closeout` = finished. Supervisors may use the rest of the
 * working range for manual override; the guard (`assertProjectTransitionAllowed`)
 * enforces who may do what. Same optional field-time / note as a task transition.
 */
export const ProjectStatusTransitionSchema = z.object({
    to: z.enum(['scheduled', 'en_route', 'on_site', 'punch', 'pending_closeout']),
    occurred_at: IsoDateTimeSchema.optional().describe('field time; defaults to server now'),
    note: z.string().max(2_000).optional(),
});
/** One entry of a project's typed status timeline (direct project work, A3/A5). */
export const ProjectStatusEventSchema = z.object({
    id: UuidSchema,
    from_status: ProjectStatusSchema.nullable(),
    to_status: ProjectStatusSchema,
    occurred_at: IsoDateTimeSchema,
    actor_name: z.string().nullable(),
    note: z.string().nullable(),
});
export const ProjectStatusEventsResponseSchema = z.object({
    project_id: UuidSchema,
    events: z.array(ProjectStatusEventSchema),
});
/**
 * Bid editor input (§A). The supervisor enters a single combined labor figure,
 * round-trip miles, and a debris tier (0–4 quarters); the API computes the cents
 * + total via computeBid against the current cost settings. show_cost +
 * recipient travel with the bid for the approval email.
 */
export const ProjectBidUpdateSchema = z
    .object({
    labor_cents: MoneyCentsSchema.nullable().optional(),
    // Labor calc input (§A6f) — per-role lines (count + hours each). When sent
    // the server reconciles crew/man-hours/blended-rate/labor_cents from the
    // current labor_rates (authoritative); otherwise labor_cents is a flat figure.
    labor_roles: CrewRoleLinesSchema.nullable().optional(),
    mileage_miles: z.number().nonnegative().max(1_000_000).nullable().optional(),
    // Saved journey (§A6g) — when sent, the server recomputes mileage_miles from
    // the legs (authoritative); null clears it back to manual entry.
    mileage_legs: MileageItinerarySchema.nullable().optional(),
    debris_quarters: z.number().int().min(0).max(4).nullable().optional(),
    show_cost: z.boolean().optional(),
    recipient_name: z.string().max(200).nullable().optional(),
    recipient_email: z.string().email().max(200).nullable().optional(),
    bid_mode: BidModeSchema.optional(),
})
    .refine((d) => Object.keys(d).length > 0, {
    message: 'Provide at least one bid field to update.',
});
/**
 * The canonical Vari pickup-origin warehouses (VCS-203). The PM picks from these
 * (plus a free-text "Other"); the packing-slip + chat extractors ground their
 * "Shipping From" / pickup origin against this exact list so origins land as
 * canonical names. Single source of truth — the intake form's dropdown AND both
 * AI prompts (chat + PDF) read it, so adding a warehouse here updates everywhere.
 *
 * `city`/`state` ride along so the AI grounding can match a "Shipping From" block
 * by LOCATION, not just by name. Keep the names in sync with the seeded
 * `locations` rows (apps/api/src/seed/data.ts) the trip calculator reads.
 */
export const CANONICAL_WAREHOUSE_DIRECTORY = [
    { name: 'Northlake Warehouse', city: 'Northlake', state: 'TX' },
    { name: 'Vari HQ — Coppell', city: 'Coppell', state: 'TX' },
    { name: 'VariSpace Southlake', city: 'Southlake', state: 'TX' },
    { name: 'VariSpace Coppell', city: 'Coppell', state: 'TX' },
    { name: 'Dark Warehouse', city: null, state: 'TX' },
];
/** Just the canonical names — the intake form's origin-warehouse dropdown
 *  options, and the closed set the AI must return for `origin_warehouse`. */
export const CANONICAL_WAREHOUSES = CANONICAL_WAREHOUSE_DIRECTORY.map((w) => w.name);
/** Format warehouses as "- Name — City, ST" grounding lines for the AI
 *  extractors, so the model matches a "Shipping From" block by LOCATION, not just
 *  by name. A warehouse with no city degrades to just its name. */
export function warehouseGroundingLines(warehouses, indent = '') {
    return warehouses
        .map((w) => {
        const where = w.city ? ` — ${w.city}${w.state ? `, ${w.state}` : ''}` : '';
        return `${indent}- ${w.name}${where}`;
    })
        .join('\n');
}
// ----------------------------------------------------------------------------
// Create payload — what an intake form / AI / PDF intake produces
// ----------------------------------------------------------------------------
export const ProjectCreateSchema = z.object({
    title: z.string().min(1).max(200),
    client_name: z.string().min(1).max(200),
    source: ProjectSourceSchema.default('form'),
    kind: ProjectKindSchema.default('install'),
    priority: PrioritySchema.default('normal'),
    // Optional SAP / Salesforce linkage
    sap_order_number: z.string().optional().nullable(),
    sap_project_id: z.string().optional().nullable(),
    customer_number: z.string().optional().nullable(),
    design_job_number: z.string().optional().nullable(),
    design_pd_number: z.string().optional().nullable(),
    /** Parent project this follows (e.g. a punch visit after its install). */
    parent_project_id: UuidSchema.optional().nullable(),
    // Site — either a registered location or an inline address
    site_location_id: UuidSchema.optional().nullable(),
    site_address: AddressSchema.optional().nullable(),
    site_contact_name: z.string().optional().nullable(),
    site_contact_phone: PhoneSchema.optional().nullable(),
    /** Site access / location notes — optional free text (gate codes, parking…). */
    site_notes: z.string().max(2_000).optional().nullable(),
    /** Pickup origin warehouses — canonical names and/or PM-entered "Other". */
    origin_warehouse: z.array(z.string().max(200)).optional().nullable(),
    // PM ownership
    pm_user_id: UuidSchema.optional().nullable(),
    interior_designer: z.string().optional().nullable(),
    interior_designer_email: z.string().optional().nullable(),
    csm: z.string().optional().nullable(),
    csm_email: z.string().optional().nullable(),
    sales_executive: z.string().optional().nullable(),
    sales_executive_email: z.string().optional().nullable(),
    status_update_recipients: z.array(StatusUpdateRecipientSchema).optional().nullable(),
    // Money
    delivery_install_budget_cents: MoneyCentsSchema.optional().nullable(),
    /**
     * Supervisor-only bid inputs (§A6b). When present, the server computes the
     * full bid (labor + mileage + debris → total) via computeBid against the
     * current cost settings and stores it on the project — so the complete budget
     * is captured at create time and feeds both the sign-off email and
     * budget-vs-actual. Ignored for PM-created requests.
     */
    bid_labor_cents: MoneyCentsSchema.optional().nullable(),
    bid_labor_roles: CrewRoleLinesSchema.optional().nullable(),
    bid_mileage_miles: z.number().nonnegative().max(1_000_000).optional().nullable(),
    bid_mileage_legs: MileageItinerarySchema.optional().nullable(),
    bid_debris_quarters: z.number().int().min(0).max(4).optional().nullable(),
    /**
     * Supervisor-only internal sign-off (§A6, the "Kelsey case"). When
     * `require_signoff` is true and a recipient is given, a supervisor-created
     * project is held as `requested` with a proposed schedule and an approval
     * email is sent — instead of auto-scheduling — so an internal stakeholder
     * signs off first. The optional budget above rides along as the bid total
     * (shown to the recipient only when `signoff_show_cost` is on, mirroring
     * `bid_show_cost`). All four are ignored for PM-created requests.
     */
    require_signoff: z.boolean().optional(),
    signoff_recipient_name: z.string().max(200).optional().nullable(),
    signoff_recipient_email: z.string().email().max(320).optional().nullable(),
    signoff_show_cost: z.boolean().optional(),
    // Notes
    intake_notes: z.string().max(10_000).optional().nullable(),
    // Schedule preferences. Required at intake (VCS-201/202): a request must name
    // a concrete start (day + time) and a hard deadline. requested_end_date stays
    // optional — it's only set for a multi-day window.
    requested_start_date: IsoDateTimeSchema,
    requested_end_date: IsoDateTimeSchema.optional().nullable(),
    deadline_date: IsoDateTimeSchema,
    /** PM's requested per-day working hours (same-for-all or per-day). */
    requested_schedule: ScheduleDaysInputSchema.optional().nullable(),
    /** When true, working hours are flexible ("come whenever"); day windows are
     *  advisory placeholders the supervisor can firm up on approval. */
    requested_schedule_flexible: z.boolean().optional(),
    /**
     * Input-only task-breakdown hint, materialized into real task rows on
     * create and never stored on the project. The PDF intake passes the
     * extractor's line-item-aware proposal here so the supervisor starts from a
     * richer breakdown; chat/form omit it and the server derives a default
     * breakdown from `kind`. NOT a PM-authored field — the intake UI never shows
     * a task editor (PMs are requesters, not operators); the supervisor reviews
     * and edits these on the project screen before approving. Validated by shape,
     * so a hand-crafted call still can't inject malformed tasks.
     */
    proposed_tasks: z.array(IntakeProposedTaskSchema).max(50).optional(),
});
// ----------------------------------------------------------------------------
// Update / patch payload
// ----------------------------------------------------------------------------
// `status` is deliberately NOT patchable: it moves only through the dedicated
// flows (approve, task-transition rollup, block/unblock, close) — a free-form
// status write bypassed the approval gate and the audit trail (audit H1).
export const ProjectUpdateSchema = ProjectCreateSchema.partial().extend({
    internal_notes: z.string().max(10_000).optional().nullable(),
    recap_narrative: z.string().max(10_000).optional().nullable(),
    actual_cost_cents: MoneyCentsSchema.optional().nullable(),
    completed_at: IsoDateTimeSchema.optional().nullable(),
});
/** Block a project (alarm state). The previous status is preserved server-side
 *  and restored on unblock. */
export const ProjectBlockSchema = z.object({
    reason: z.string().min(1).max(2000),
});
// ----------------------------------------------------------------------------
// Approve payload (SCH.3) — the supervisor commits a target schedule when
// approving. Day strings (business-local); the API seeds the task times from
// them so the project gets a real scheduled window. Both optional: an empty
// body approves without committing a schedule yet.
// ----------------------------------------------------------------------------
const ApproveDaySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
const ApproveTimeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'must be HH:MM');
export const ProjectApproveSchema = z.object({
    scheduled_start_date: ApproveDaySchema.optional().nullable(),
    scheduled_end_date: ApproveDaySchema.optional().nullable(),
    /** Local time the crew arrives for the install / non-pickup tasks (default 08:00). */
    install_time: ApproveTimeSchema.optional().nullable(),
    /** Warehouse pickup day — the supervisor commits this and the PM is notified so
     *  they can coordinate the warehouse (VCS-301/302). Maps to the pickup task(s). */
    pickup_date: ApproveDaySchema.optional().nullable(),
    /** Pickup window — Mon–Fri 09:00 or 14:00 by default; supervisor may override. */
    pickup_time: ApproveTimeSchema.optional().nullable(),
    /** Committed per-day working hours for the non-pickup work (same or per-day).
     *  When present, each day's start_time seeds that day's tasks. */
    schedule: ScheduleDaysInputSchema.optional().nullable(),
});
/** Supervisor bounces a request back to the PM (SCH.5) — a note + an optional
 *  proposed start, instead of approving. The project stays 'requested'. */
export const ProjectRequestChangesSchema = z.object({
    note: z.string().min(1).max(2000),
    proposed_start_date: IsoDateTimeSchema.optional().nullable(),
    /** Include the project's bid total on the bounce so the requestor sees the
     *  budget (§A6c). Reads the already-saved bid; no cost is shown if false. */
    share_budget: z.boolean().optional(),
});
/** PM revises a sent-back request and resubmits (SCH.5) — optionally with a new
 *  desired window — clearing the bounce and re-entering the supervisor's queue.
 *  When `requested_schedule` is sent, the API derives the start/end bounds from
 *  it (first day's start, last day's end) so the request's hours stay in sync
 *  with its dates (VCS-202). */
export const ProjectResubmitSchema = z.object({
    requested_start_date: IsoDateTimeSchema.optional().nullable(),
    requested_end_date: IsoDateTimeSchema.optional().nullable(),
    requested_schedule: ScheduleDaysInputSchema.optional().nullable(),
});
/** Supervisor moves an already-scheduled project to new days/hours (e.g. by
 *  dragging it on the calendar). The new per-day schedule becomes the committed
 *  `approved_schedule`, tasks are re-seeded, and the requestor + crew are
 *  notified — no re-approval (Calendar Phase 2). */
export const ProjectRescheduleSchema = z.object({
    schedule: ScheduleDaysInputSchema,
});
// ----------------------------------------------------------------------------
// Query filters for the project list
// ----------------------------------------------------------------------------
/**
 * Columns the projects list can be sorted by. Each maps 1:1 to a `projects`
 * column, so the API can pass the value straight to `orderBy` (allowlisted —
 * never free text). The default is `created_at` desc (newest first).
 */
export const PROJECT_SORT_FIELDS = [
    'created_at',
    'title',
    'client_name',
    'status',
    'priority',
    'requested_start_date',
    'delivery_install_budget_cents',
];
export const ProjectSortFieldSchema = z.enum(PROJECT_SORT_FIELDS);
export const SortOrderSchema = z.enum(['asc', 'desc']);
export const ProjectListQuerySchema = PageQuerySchema.extend({
    status: ProjectStatusSchema.optional(),
    pm_user_id: UuidSchema.optional(),
    priority: PrioritySchema.optional(),
    q: z.string().max(200).optional().describe('free-text search on title / client / SAP IDs'),
    from_date: IsoDateTimeSchema.optional(),
    to_date: IsoDateTimeSchema.optional(),
    sort: ProjectSortFieldSchema.optional().describe('sort column (default created_at)'),
    order: SortOrderSchema.optional().describe('sort direction (default desc — newest first)'),
});
// ----------------------------------------------------------------------------
// List response
// ----------------------------------------------------------------------------
export const ProjectListResponseSchema = PageEnvelope(ProjectSchema);
// ----------------------------------------------------------------------------
// Request-review summary (EX.8) — the supervisor's decision surface. Each
// pending request is enriched with the signals a supervisor needs to approve
// with eyes open: the labor estimate vs budget (recomputed from the request's
// tasks), back-ordered items, site-readiness flags, and document/task counts.
// ----------------------------------------------------------------------------
export const RequestReviewLaborSchema = z.object({
    total_task_minutes: z.number(),
    total_person_hours: z.number(),
    estimated_labor_cents: z.number().int(),
    budget_cents: z.number().int().nullable(),
    budget_ratio: z.number().nullable(),
    over_budget: z.boolean(),
    headroom_cents: z.number().int().nullable(),
});
export const RequestReviewSummarySchema = z.object({
    id: UuidSchema,
    title: z.string(),
    client_name: z.string(),
    kind: ProjectKindSchema,
    priority: PrioritySchema,
    source: ProjectSourceSchema,
    created_at: IsoDateTimeSchema,
    requested_start_date: IsoDateTimeSchema.nullable(),
    deadline_date: IsoDateTimeSchema.nullable(),
    requested_end_date: IsoDateTimeSchema.nullable(),
    /** PM's requested per-day hours — pre-fills the approve dialog. */
    requested_schedule: z.array(ScheduleDaySchema).nullable(),
    pm_display_name: z.string().nullable(),
    delivery_install_budget_cents: MoneyCentsSchema.nullable(),
    origin_warehouse: z.array(z.string().max(200)).nullable(),
    /** Labor estimate vs budget, recomputed from the request's tasks. Null when
     *  the request has no tasks to estimate from. */
    labor: RequestReviewLaborSchema.nullable(),
    /** Packing-slip line items short of their ordered quantity — install blockers. */
    back_order_count: z.number().int(),
    /** Site-prep / power / electrical caveats extracted from the design package. */
    readiness_flag_count: z.number().int(),
    /** Source PDFs attached (sales order / packing slip / design package). */
    document_count: z.number().int(),
    task_count: z.number().int(),
});
export const RequestReviewListSchema = z.object({
    data: z.array(RequestReviewSummarySchema),
});
/**
 * Suggested pickup days for consolidation (demo #7, F20): days on OTHER projects
 * that already have a pickup scheduled, so a supervisor can put this project's
 * pickup on a day a truck is already going out. The client filters to days before
 * the chosen install.
 */
export const PickupSuggestionSchema = z.object({
    date: z.string(), // YYYY-MM-DD (business-local)
    pickup_count: z.number().int(),
    projects: z.array(z.string()),
});
export const PickupSuggestionsResponseSchema = z.object({
    suggestions: z.array(PickupSuggestionSchema),
});
//# sourceMappingURL=projects.js.map